import {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import type { ReactNode } from "react";
import {
  downloadExport,
  loadReport,
  loadSummary,
  loadSyncStatus,
  refreshReport
} from "./api";
import portalLogo from "../../../logo-baza-biznesa.png";
import { createServerSession } from "./bitrix";
import { demoReport, demoSummary } from "./demo";
import type {
  Collab,
  Filters,
  MemberRow,
  ReportResponse,
  ReportType,
  SessionResponse,
  Summary
} from "./types";

const initialFilters: Filters = {
  search: "",
  position: "all",
  absenceMonths: "",
  status: "all",
  page: 1
};

type SidebarSection = "overview" | ReportType;

export function App() {
  const demoMode =
    import.meta.env.DEV &&
    new URLSearchParams(window.location.search).get("demo") === "1";
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [report, setReport] = useState<ReportResponse | null>(null);
  const [type, setType] = useState<ReportType>("collabs");
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [booting, setBooting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [portalMenuOpen, setPortalMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<SidebarSection>("overview");
  const portalMenuRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const reportSectionRef = useRef<HTMLElement>(null);
  const deferredSearch = useDeferredValue(filters.search);

  useEffect(() => {
    if (!portalMenuOpen && !sidebarOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!portalMenuRef.current?.contains(event.target as Node)) {
        setPortalMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPortalMenuOpen(false);
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [portalMenuOpen, sidebarOpen]);

  useEffect(() => {
    if (demoMode) {
      setSession({
        token: "demo",
        expiresIn: 600,
        portal: "demo.bitrix24.ru",
        user: { id: 1, name: "Марина Волкова", isAdmin: true }
      });
      setSummary(demoSummary);
      setBooting(false);
      return;
    }
    let cancelled = false;
    void createServerSession()
      .then(async (created) => {
        if (cancelled) return;
        const typedSession: SessionResponse = {
          ...created,
          expiresIn: 600
        };
        setSession(typedSession);
        const nextSummary = await loadSummary(created.token);
        if (!cancelled) setSummary(nextSummary);
      })
      .catch((reason) => {
        if (!cancelled) {
          setError(
            reason instanceof Error
              ? reason.message
              : "Не удалось открыть приложение."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setBooting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [demoMode]);

  useEffect(() => {
    if (!session) return;
    if (demoMode) {
      setReport(demoReport(type, { ...filters, search: deferredSearch }));
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void loadReport(
      session.token,
      type,
      { ...filters, search: deferredSearch },
      controller.signal
    )
      .then(setReport)
      .catch((reason) => {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setError(
          reason instanceof Error
            ? reason.message
            : "Не удалось загрузить отчёт."
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [
    session,
    demoMode,
    type,
    deferredSearch,
    filters.position,
    filters.absenceMonths,
    filters.status,
    filters.page
  ]);

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: key === "page" ? Number(value) : 1
    }));
  };

  const changeType = (nextType: ReportType) => {
    setReport(null);
    setType(nextType);
    setActiveSection(nextType);
    setFilters((current) => ({ ...current, page: 1 }));
  };

  const navigateSidebar = (section: SidebarSection) => {
    setPortalMenuOpen(false);
    setSidebarOpen(false);
    if (section === "overview") {
      setActiveSection("overview");
      mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    changeType(section);
    reportSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleRefresh = async () => {
    if (!session || refreshing) return;
    if (demoMode) {
      setSummary({ ...demoSummary, generatedAt: new Date().toISOString() });
      setReport(demoReport(type, filters));
      return;
    }
    setRefreshing(true);
    setError("");
    const acceptedAt = Date.now();
    try {
      await refreshReport(session.token);
      for (let attempt = 0; attempt < 90; attempt += 1) {
        await wait(1000);
        const status = await loadSyncStatus(session.token);
        const startedAt = status.sync?.started_at
          ? new Date(status.sync.started_at).getTime()
          : 0;
        if (startedAt < acceptedAt - 2000) continue;
        if (status.sync?.status === "failed") {
          throw new Error("Обновление отчёта завершилось ошибкой.");
        }
        if (status.sync?.status === "completed") break;
      }
      const [nextSummary, nextReport] = await Promise.all([
        loadSummary(session.token),
        loadReport(session.token, type, filters)
      ]);
      setSummary(nextSummary);
      setReport(nextReport);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Не удалось обновить отчёт."
      );
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = async (format: "csv" | "xlsx") => {
    if (!session) return;
    if (demoMode) {
      setError("Экспорт в деморежиме отключён.");
      return;
    }
    try {
      setError("");
      await downloadExport(session.token, type, format, filters);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Не удалось скачать отчёт."
      );
    }
  };

  if (booting) {
    return <CenteredState title="Подключаемся к Битрикс24…" loading />;
  }
  if (!session) {
    return (
      <CenteredState
        title="Приложение недоступно"
        message={error || "Не удалось создать защищённую сессию."}
      />
    );
  }

  const pages = Math.max(
    1,
    Math.ceil((report?.total ?? 0) / (report?.pageSize ?? 50))
  );

  return (
    <div className={`app-shell ${sidebarOpen ? "sidebar-open" : ""}`}>
      <button
        className="sidebar-overlay"
        type="button"
        aria-label="Закрыть меню"
        onClick={() => {
          setPortalMenuOpen(false);
          setSidebarOpen(false);
        }}
      />
      <aside className="sidebar" aria-label="Меню приложения">
        <nav className="sidebar-menu" aria-label="Разделы отчёта">
          <SidebarMenuItem
            label="Обзор"
            active={activeSection === "overview"}
            icon={<OverviewIcon />}
            onClick={() => navigateSidebar("overview")}
          />
          <SidebarMenuItem
            label="Коллабы"
            active={activeSection === "collabs"}
            icon={<CollabsIcon />}
            onClick={() => navigateSidebar("collabs")}
          />
          <SidebarMenuItem
            label="Участники"
            active={activeSection === "members"}
            icon={<MembersIcon />}
            onClick={() => navigateSidebar("members")}
          />
          <SidebarMenuItem
            label="Гости"
            active={activeSection === "guests"}
            icon={<GuestsIcon />}
            onClick={() => navigateSidebar("guests")}
          />
        </nav>
        <div className="portal-dropdown footer-menu" ref={portalMenuRef}>
          {portalMenuOpen && <PortalMenu onNavigate={() => setPortalMenuOpen(false)} />}
          <button
            className="portal-brand"
            type="button"
            aria-label="Меню База Бизнеса"
            aria-haspopup="menu"
            aria-expanded={portalMenuOpen}
            onClick={() => setPortalMenuOpen((current) => !current)}
          >
            <img className="portal-logo" src={portalLogo} alt="База Бизнеса" />
            <span className="portal-name">База Бизнеса</span>
            <span className="portal-menu" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 7h16" />
                <path d="M4 12h16" />
                <path d="M4 17h16" />
              </svg>
            </span>
          </button>
        </div>
      </aside>
      <main className="main" ref={mainRef}>
      <header className="top-bar">
        <button
          className="sidebar-toggle"
          type="button"
          aria-label="Открыть меню"
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen(true)}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 7h16" />
            <path d="M4 12h16" />
            <path d="M4 17h16" />
          </svg>
        </button>
        <div>
          <h1 className="page-title">Анализ гостей на портале</h1>
          <p className="page-subtitle">
            Аналитика гостей и участников по доступным коллабам Битрикс24.
          </p>
        </div>
      </header>

        <div className="page-divider" />

        <section className="section report-parameters">
          <div className="section-head">
            <div>
              <h2>Параметры отчета</h2>
              <p>
                Поиск работает по ФИО, должности, названию коллабы, собственнику
                и модераторам.
              </p>
            </div>
            <div className="report-tabs" role="tablist" aria-label="Тип отчёта">
              {(["collabs", "members", "guests"] as ReportType[]).map((item) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={type === item}
                  className={`tab-button ${type === item ? "is-active" : ""}`}
                  onClick={() => changeType(item)}
                  key={item}
                >
                  {tabLabel(item)}
                </button>
              ))}
            </div>
          </div>

          <div className="field-grid report-controls">
            <label className="field search-field">
              <span>Поиск человека или коллабы</span>
              <input
                type="search"
                placeholder="Например: Анна, Продажи, HR"
                value={filters.search}
                onChange={(event) => setFilter("search", event.target.value)}
              />
            </label>
            <label className="field">
              <span>Должность</span>
              <select
                value={filters.position}
                onChange={(event) => setFilter("position", event.target.value)}
              >
                <option value="all">Все должности</option>
                <option value="__missing">Не заполнена</option>
                {summary?.positions.map((position) => (
                  <option value={position} key={position}>
                    {position}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Отсутствие на портале</span>
              <select
                value={filters.absenceMonths}
                onChange={(event) => setFilter("absenceMonths", event.target.value)}
              >
                <option value="">Все участники</option>
                <option value="3">Больше 3 месяцев</option>
                <option value="6">Больше 6 месяцев</option>
              </select>
            </label>
            <label className="field">
              <span>Статус коллабы</span>
              <select
                value={filters.status}
                onChange={(event) =>
                  setFilter("status", event.target.value as Filters["status"])
                }
              >
                <option value="all">Все</option>
                <option value="active">Активные</option>
                <option value="archive">Архив</option>
              </select>
            </label>
          </div>

          <div className="filter-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? "Обновляем…" : "Обновить"}
            </button>
          </div>
          {error && <div className="load-status is-error">{error}</div>}
        </section>

        <section className="section stats-section" aria-label="Сводка">
          <SummaryCard
            label="Коллаб"
            value={summary?.collabs ?? 0}
            onClick={() => changeType("collabs")}
          />
          <SummaryCard
            label="Участников"
            value={summary?.uniqueMembers ?? 0}
            onClick={() => changeType("members")}
            detail={summary?.uniqueGuests ?? 0}
          />
        </section>

        <section className="section report-results" ref={reportSectionRef}>
          <div className="section-head">
            <div>
              <h2>{reportTitle(type)}</h2>
              <p>{reportDescription(type)}</p>
            </div>
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={() => handleExport("csv")}>
                CSV
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => handleExport("xlsx")}
              >
                Excel
              </button>
            </div>
          </div>

          <div className={`table-wrap table-shell ${loading ? "is-loading" : ""}`}>
            {type === "collabs" ? (
              <CollabTable rows={(report?.rows ?? []) as Collab[]} />
            ) : (
              <MemberTable
                rows={(report?.rows ?? []) as MemberRow[]}
                guestMode={type === "guests"}
              />
            )}
            {loading && (
              <div className="table-loader" role="status">
                <span className="spinner" />
                Загружаем данные…
              </div>
            )}
          </div>

          {pages > 1 && (
            <div className="pagination">
              <span>
                Найдено: <strong>{report?.total ?? 0}</strong>
              </span>
              <div>
                <button
                  type="button"
                  disabled={filters.page <= 1}
                  onClick={() => setFilter("page", filters.page - 1)}
                >
                  Назад
                </button>
                <span>
                  {filters.page} / {pages}
                </span>
                <button
                  type="button"
                  disabled={filters.page >= pages}
                  onClick={() => setFilter("page", filters.page + 1)}
                >
                  Далее
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function SidebarMenuItem(props: {
  label: string;
  active: boolean;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      className={`sidebar-menu-item ${props.active ? "is-active" : ""}`}
      type="button"
      aria-current={props.active ? "page" : undefined}
      onClick={props.onClick}
    >
      <span className="sidebar-menu-icon" aria-hidden="true">{props.icon}</span>
      <span>{props.label}</span>
    </button>
  );
}

function OverviewIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5l9-6 9 6" />
      <path d="M5 10.5v9h5v-6h4v6h5v-9" />
    </svg>
  );
}

function CollabsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="3" />
      <circle cx="17" cy="7" r="3" />
      <circle cx="12" cy="17" r="3" />
      <path d="M9.5 8.5l1.4 5.6M14.5 8.5l-1.4 5.6M10 7h4" />
    </svg>
  );
}

function MembersIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M3.5 19c.4-3.3 2.3-5 5.5-5s5.1 1.7 5.5 5M14 15c3.4-.8 5.5.5 6.5 3.5" />
    </svg>
  );
}

function GuestsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19c.4-3.3 2.3-5 5.5-5 1.5 0 2.7.4 3.6 1.1" />
      <path d="M17 12v7M13.5 15.5h7" />
    </svg>
  );
}

function PortalMenu({ onNavigate }: { onNavigate: () => void }) {
  return (
    <div className="portal-dropdown-menu" role="menu">
      <PortalMenuLink
        href="https://bazabz.ru/?from=pr-freeslots"
        label="Сайт"
        icon={<HomeIcon />}
        onNavigate={onNavigate}
      />
      <PortalMenuLink
        href="https://bazabz.ru/politika_konfidentsialnosti"
        label="Политика конфиденциальности"
        icon={<ShieldIcon />}
        onNavigate={onNavigate}
      />
      <PortalMenuLink
        href="https://bazabz.ru/lic-sogl/analiz-gostei-na-portale"
        label="Пользовательское соглашение"
        icon={<DocumentIcon />}
        onNavigate={onNavigate}
      />
      <PortalMenuLink
        href="https://auth2.bitrix24.net/oauth/select/?preset=im&IM_DIALOG=networkLines7d430c3477dd479952f208a89afc96e3"
        label="Тех. поддержка"
        icon={<SupportIcon />}
        onNavigate={onNavigate}
      />
    </div>
  );
}

function PortalMenuLink(props: {
  href: string;
  label: string;
  icon: ReactNode;
  onNavigate: () => void;
}) {
  return (
    <a
      className="portal-dropdown-link"
      href={props.href}
      target="_blank"
      rel="noreferrer"
      role="menuitem"
      onClick={props.onNavigate}
    >
      <span className="portal-dropdown-icon" aria-hidden="true">
        {props.icon}
      </span>
      <span>{props.label}</span>
    </a>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a15 15 0 0 1 0 18" />
      <path d="M12 3a15 15 0 0 0 0 18" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4z" />
      <path d="M9.5 12.5l1.5 1.5 3.5-3.5" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 9l4-4 4 4" />
      <path d="M20 9l-4-4-4 4" />
      <path d="M7 12l2 2c1 1 3 1 4 0l2-2" />
      <path d="M5 11l2 2" />
      <path d="M19 11l-2 2" />
    </svg>
  );
}

function SupportIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.9.4-1.3.9-1.3 1.7" />
      <circle cx="12" cy="17.5" r=".8" fill="currentColor" stroke="none" />
    </svg>
  );
}

function SummaryCard(props: {
  label: string;
  value: number;
  onClick: () => void;
  detail?: number;
}) {
  return (
    <button
      type="button"
      className="stat-card stat-button"
      onClick={props.onClick}
    >
      <span>{props.label}</span>
      {props.detail === undefined ? (
        <strong>{props.value.toLocaleString("ru-RU")}</strong>
      ) : (
        <div className="stat-card-detail">
          <div className="stat-list">
            <div className="stat-line">
              <span className="stat-line-label">Всего</span>
              <span className="stat-line-dots" aria-hidden="true" />
              <span className="stat-line-value">{props.value.toLocaleString("ru-RU")}</span>
            </div>
            <div className="stat-line">
              <span className="stat-line-label">Из них гостей</span>
              <span className="stat-line-dots" aria-hidden="true" />
              <span className="stat-line-value">{props.detail.toLocaleString("ru-RU")}</span>
            </div>
          </div>
        </div>
      )}
    </button>
  );
}

function CollabTable({ rows }: { rows: Collab[] }) {
  if (!rows.length) return <EmptyState />;
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Коллаба</th>
            <th>Владелец коллабы</th>
            <th>Дата создания</th>
            <th>Модераторы</th>
            <th>Участники</th>
            <th>Статус</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((collab) => (
            <tr key={collab.id}>
              <td>
                <strong className="entity-name">{collab.name}</strong>
                <span className="muted">ID {collab.id}</span>
              </td>
              <td>{collab.owner.fullName}</td>
              <td>{formatDate(collab.createdAt)}</td>
              <td>
                <Pills values={collab.moderators.map((person) => person.fullName)} />
              </td>
              <td>
                <Pills values={collab.members.map((person) => person.fullName)} />
              </td>
              <td>
                <StatusBadge status={collab.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MemberTable({
  rows,
  guestMode
}: {
  rows: MemberRow[];
  guestMode: boolean;
}) {
  if (!rows.length) return <EmptyState />;
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Коллаба</th>
            <th>Владелец коллабы</th>
            <th>Дата создания коллабы</th>
            <th>{guestMode ? "Гость" : "Участник"}</th>
            <th>Тип</th>
            <th>Должность</th>
            <th>Последняя активность</th>
            <th>Отсутствие</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.collab.id}:${row.member.id}`}>
              <td>
                <strong className="entity-name">{row.collab.name}</strong>
                <span className="muted">ID {row.collab.id}</span>
              </td>
              <td>{row.collab.owner.fullName}</td>
              <td>{formatDate(row.collab.createdAt)}</td>
              <td>
                <strong className="person-name">
                  {displayMemberName(row.member.fullName, guestMode)}
                </strong>
                <span className="muted">
                  {row.member.email || row.member.phone || "Контакт не найден"}
                </span>
              </td>
              <td>
                <span
                  className={`type-badge ${
                    row.member.userType === "guest" ? "guest" : ""
                  }`}
                >
                  {row.member.userType === "guest" ? "Гость" : "Участник"}
                </span>
              </td>
              <td>{row.member.position || <span className="missing">Не заполнена</span>}</td>
              <td>{formatDate(row.member.lastActivityAt)}</td>
              <td>
                {!row.member.lastActivityAt
                  ? <span className="missing">Нет данных</span>
                  : formatAbsenceSince(row.member.lastActivityAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pills({ values }: { values: string[] }) {
  if (!values.length) return <span className="missing">—</span>;
  const visible = values.slice(0, 3);
  return (
    <div className="pills">
      {visible.map((value) => (
        <span key={value}>{value}</span>
      ))}
      {values.length > visible.length && (
        <span className="more">+{values.length - visible.length}</span>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Collab["status"] }) {
  return (
    <span className={`status-badge ${status}`}>
      {status === "active" ? "Активна" : "Архив"}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="empty-state">
      Ничего не найдено. Попробуйте изменить поиск или фильтр.
    </div>
  );
}

function CenteredState(props: {
  title: string;
  message?: string;
  loading?: boolean;
}) {
  return (
    <div className="centered-state">
      <div>
        {props.loading && <span className="spinner large" />}
        <h1>{props.title}</h1>
        {props.message && <p>{props.message}</p>}
      </div>
    </div>
  );
}

function reportTitle(type: ReportType): string {
  if (type === "guests") return "Гости коллаб";
  if (type === "members") return "Участники";
  return "Коллабы и состав";
}

function reportDescription(type: ReportType): string {
  if (type === "guests") {
    return "Внешние участники с контактами и последней активностью.";
  }
  if (type === "members") {
    return "Полный список участников по коллабам: ФИО, должность и даты активности.";
  }
  return "Список коллаб с владельцем, модераторами и входящими участниками.";
}

function tabLabel(type: ReportType): string {
  if (type === "guests") return "Гости";
  if (type === "members") return "Участники";
  return "Коллабы";
}

function formatDate(value?: string | null): string {
  if (!value) return "Нет данных";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Нет данных"
    : new Intl.DateTimeFormat("ru-RU").format(date);
}

function formatDateTime(value?: string): string {
  if (!value) return "Ещё не обновлялся";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Нет данных"
    : new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "short",
        timeStyle: "short"
      }).format(date);
}

function formatAbsence(months: number): string {
  if (months === 0) return "Менее месяца";
  return `${months} ${pluralizeMonths(months)}`;
}

function formatAbsenceSince(lastActivityAt: string): string {
  const lastDate = new Date(lastActivityAt);
  if (Number.isNaN(lastDate.getTime())) return "Нет данных";
  const now = new Date();
  if (lastDate > now) return "0 дней";
  let months =
    (now.getFullYear() - lastDate.getFullYear()) * 12 +
    now.getMonth() -
    lastDate.getMonth();
  let days = now.getDate() - lastDate.getDate();
  if (days < 0) {
    months -= 1;
    const previousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += previousMonth.getDate();
  }
  if (months > 6 || (months === 6 && days > 0)) return "более 6 месяцев";
  const parts: string[] = [];
  if (months > 0) parts.push(`${months} ${pluralizeMonths(months)}`);
  if (days > 0 || parts.length === 0) parts.push(`${days} ${pluralizeDays(days)}`);
  return parts.join(" ");
}

function displayMemberName(value: string, guestMode: boolean): string {
  if (!value || /^ID\s|^Неизвестный пользователь$/i.test(value)) {
    return guestMode ? "ФИО гостя не заполнено" : "ФИО участника не заполнено";
  }
  return value;
}

function pluralizeDays(value: number): string {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "дня";
  return "дней";
}

function pluralizeMonths(value: number): string {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return "месяц";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return "месяца";
  }
  return "месяцев";
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}
